import {BackendFetch} from "@tolgee/react";
import type {BackendMiddleware, PluginTools, TolgeePlugin} from "@tolgee/react";

type BackendFetchOptions = Parameters<typeof BackendFetch>[0];

export type RetryOptions = {
  // Number of extra attempts after the first, so `retries: 2` makes up to three
  // fetches in total.
  retries: number;
  // Delay before the first retry; each subsequent retry waits twice as long.
  backoffMs: number;
};

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

// A translation record is fetched once, on the cold initial page load, before
// the UI can render its text. That fetch is a mandatory network request against
// /static/i18n on the page's own origin, and a transient failure (dropped
// connection, timeout, a content blocker) otherwise leaves the visitor looking
// at raw translation keys until they reload. Retrying the fetch recovers the
// blip before Tolgee turns it into a RecordFetchError, so only a record that is
// still unreachable after every attempt surfaces as an error.
export function retryingBackend(backend: BackendMiddleware, {retries, backoffMs}: RetryOptions): BackendMiddleware {
  return {
    getRecord: async data => {
      let lastError: unknown;
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          return await backend.getRecord(data);
        } catch (error) {
          lastError = error;
          if (attempt < retries) {
            await sleep(backoffMs * 2 ** attempt);
          }
        }
      }
      throw lastError;
    },
  };
}

// `BackendFetch` wired to retry transient fetch failures. It delegates to the
// standard plugin and wraps whatever backend that plugin registers, so path
// building, timeouts, and data extraction stay with Tolgee.
export function RetryingBackendFetch(options: BackendFetchOptions, retryOptions: RetryOptions): TolgeePlugin {
  const inner = BackendFetch(options);
  return (tolgee, tools) => {
    const wrappedTools: PluginTools = {
      ...tools,
      addBackend: backend => tools.addBackend(backend && retryingBackend(backend, retryOptions)),
    };
    return inner(tolgee, wrappedTools);
  };
}
